/**
 * @file OrdersView.tsx
 *
 * Orders tab screen. Fetches a server-paginated order list with a multi-select
 * status filter and debounced search, and renders it as a table
 * ({@link OrdersTable} / {@link Pagination}) with loading / empty / no-results /
 * error states. Each row opens a details drawer ({@link OrderDetailsDrawer}); a
 * per-row contextual menu offers the state-gated actions View, Cancel
 * ({@link OrderCancelModal}), and Refund ({@link OrderRefundModal}) — the real `/v1`
 * order actions that replace the legacy status edit / delete. The drawer additionally
 * offers Delete, Mark paid, Fulfil, and the free-form Edit status
 * ({@link OrderStatusEditModal}, the admin status-override backdoor). Columns collapse
 * on smaller viewports via Gravity column-hiding utilities.
 */

import { useEffect, useRef, useState } from 'react';
import { Icon, Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { notifyError, notifySuccess } from '../feedback/toast';
import { useClickOutside } from '../hooks/use-click-outside';
import { useMediaQuery } from '../hooks/use-media-query';
import { useOrdersStore } from '../stores/orders.store';
import { useSettingsStore } from '../stores/settings.store';
import { ORDERS_PER_PAGE } from '../api/orders';
import type { ShopOrder } from '../api/shop-types';
import { EmptyState } from '../components/EmptyState';
import { Pagination } from '../components/Pagination';
import { ContextMenu } from '../components/ContextMenu';
import { ConfirmModal } from '../components/ConfirmModal';
import {
  ProductFilterDropdown,
  type FilterOption,
} from '../products/ProductFilterDropdown';
import { OrdersTable } from './OrdersTable';
import { OrderCancelModal } from './OrderCancelModal';
import { OrderRefundModal } from './OrderRefundModal';
import { OrderStatusEditModal } from './OrderStatusEditModal';
import { OrderDetailsDrawer } from './OrderDetailsDrawer';
import {
  ORDER_MENU_HEIGHT,
  ORDER_MENU_VIEWPORT_MARGIN,
  ORDER_MENU_WIDTH,
  orderMenuActions,
  type OrderMenuAction,
} from './order-menu';
import { ORDER_STATUSES, statusLabel } from './order-status';
import shared from '../shared.module.css';
import styles from './OrdersView.module.css';

interface MenuPosition {
  top?: number;
  left?: number;
}

/** Maps a `/v1` order action error code to a user-facing message (generic fallback). */
function actionErrorMessage(intl: IntlShape, code: string | null): string {
  switch (code) {
    case 'order_invalid_transition':
      return intl.formatMessage(adminMessages.ordersErrorInvalidTransition);
    case 'order_not_editable':
      return intl.formatMessage(adminMessages.ordersErrorNotEditable);
    case 'order_refund_in_progress':
      return intl.formatMessage(adminMessages.ordersErrorRefundInProgress);
    case 'order_not_deletable':
      return intl.formatMessage(adminMessages.ordersErrorNotDeletable);
    case 'order_already_paid':
      return intl.formatMessage(adminMessages.ordersErrorAlreadyPaid);
    case 'shipping.shipment_qty_exceeded':
      return intl.formatMessage(adminMessages.ordersErrorQtyExceeded);
    case 'payment_service_unavailable':
      return intl.formatMessage(adminMessages.ordersErrorPaymentUnavailable);
    default:
      return intl.formatMessage(adminMessages.ordersActionError);
  }
}

/**
 * Renders the Orders tab.
 *
 * @returns The orders screen.
 */
export function OrdersView() {
  const intl = useIntl();

  const orders = useOrdersStore((s) => s.orders);
  const ordersTotalPages = useOrdersStore((s) => s.ordersTotalPages);
  const loading = useOrdersStore((s) => s.loading);
  const error = useOrdersStore((s) => s.error);
  const fetchOrders = useOrdersStore((s) => s.fetchOrders);
  const currentOrder = useOrdersStore((s) => s.currentOrder);
  const loadOrder = useOrdersStore((s) => s.loadOrder);
  const clearOrder = useOrdersStore((s) => s.clearOrder);
  const cancelOrder = useOrdersStore((s) => s.cancelOrder);
  const refundOrder = useOrdersStore((s) => s.refundOrder);
  const deleteOrder = useOrdersStore((s) => s.deleteOrder);
  const markOrderPaid = useOrdersStore((s) => s.markOrderPaid);
  const fulfilOrder = useOrdersStore((s) => s.fulfilOrder);
  const overrideOrderStatus = useOrdersStore((s) => s.overrideOrderStatus);

  const currencySymbol = useSettingsStore((s) => s.currencySymbol);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [menuOrderId, setMenuOrderId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const [cancelTarget, setCancelTarget] = useState<ShopOrder | null>(null);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const [refundTarget, setRefundTarget] = useState<ShopOrder | null>(null);
  const [refundSaving, setRefundSaving] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<ShopOrder | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [markPaidTarget, setMarkPaidTarget] = useState<ShopOrder | null>(null);
  const [markPaidSaving, setMarkPaidSaving] = useState(false);
  const [markPaidError, setMarkPaidError] = useState<string | null>(null);

  const [fulfilTarget, setFulfilTarget] = useState<ShopOrder | null>(null);
  const [fulfilSaving, setFulfilSaving] = useState(false);
  const [fulfilError, setFulfilError] = useState<string | null>(null);

  const [statusEditTarget, setStatusEditTarget] = useState<ShopOrder | null>(null);
  const [statusEditSaving, setStatusEditSaving] = useState(false);
  const [statusEditError, setStatusEditError] = useState<string | null>(null);

  const drawerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('Mobile');

  const refetch = (): Promise<void> =>
    fetchOrders({
      page,
      perPage: ORDERS_PER_PAGE,
      status: statusFilters.join(','),
      search: debouncedSearch,
    });

  useEffect(() => {
    fetchOrders({
      page,
      perPage: ORDERS_PER_PAGE,
      status: statusFilters.join(','),
      search: debouncedSearch,
    });
  }, [fetchOrders, page, statusFilters, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const closeDrawer = (): void => {
    setSelectedId(null);
  };

  // Keep the drawer open while a cancel/refund/delete/mark-paid/fulfil/edit-status
  // dialog is layered over it.
  useClickOutside(
    drawerRef,
    closeDrawer,
    selectedId !== null &&
      cancelTarget === null &&
      refundTarget === null &&
      deleteTarget === null &&
      markPaidTarget === null &&
      fulfilTarget === null &&
      statusEditTarget === null,
  );

  const openDrawer = (order: ShopOrder): void => {
    // Drop the previous order before loading a different one: the footer actions
    // target currentOrder, which otherwise still holds the stale order while the
    // fetch is in flight (the outside-click close keeps it for the close animation).
    if (useOrdersStore.getState().currentOrder?.id !== order.id) {
      clearOrder();
    }
    setSelectedId(order.id);
    void loadOrder(order.id).then((loaded) => {
      // With the order cleared above, a failed load would otherwise be silent.
      if (!loaded) {
        notifyError(intl.formatMessage(adminMessages.toastError));
      }
    });
  };

  const closeMenu = (): void => {
    setMenuOrderId(null);
    setMenuPosition(null);
  };

  const handleMoreClick = (order: ShopOrder, triggerEl: HTMLButtonElement): void => {
    if (menuOrderId === order.id) {
      closeMenu();
      return;
    }
    if (isMobile) {
      setMenuPosition({ top: undefined, left: undefined });
    } else {
      const rect = triggerEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow >= ORDER_MENU_HEIGHT
          ? rect.bottom + 4
          : rect.top - ORDER_MENU_HEIGHT - 4;
      const left = Math.max(
        ORDER_MENU_VIEWPORT_MARGIN,
        Math.min(
          rect.right - ORDER_MENU_WIDTH,
          window.innerWidth - ORDER_MENU_WIDTH - ORDER_MENU_VIEWPORT_MARGIN,
        ),
      );
      setMenuPosition({ top, left });
    }
    setMenuOrderId(order.id);
  };

  const handleMenuSelect = (action: OrderMenuAction): void => {
    const order = orders.find((o) => o.id === menuOrderId);
    closeMenu();
    if (!order) {
      return;
    }
    if (action === 'view') {
      openDrawer(order);
    } else if (action === 'cancel') {
      setCancelError(null);
      setCancelTarget(order);
    } else if (action === 'refund') {
      setRefundError(null);
      setRefundTarget(order);
    } else {
      setDeleteError(null);
      setDeleteTarget(order);
    }
  };

  const handleCancelConfirm = async (reason: string): Promise<void> => {
    if (!cancelTarget) {
      return;
    }
    setCancelSaving(true);
    setCancelError(null);
    const ok = await cancelOrder(cancelTarget.id, reason || undefined);
    setCancelSaving(false);
    if (!ok) {
      setCancelError(
        actionErrorMessage(intl, useOrdersStore.getState().actionErrorCode),
      );
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.ordersToastCancelled));
    setCancelTarget(null);
    await refetch();
  };

  const handleRefundConfirm = async ({
    reason,
    restock,
  }: {
    reason: string;
    restock: boolean;
  }): Promise<void> => {
    if (!refundTarget) {
      return;
    }
    setRefundSaving(true);
    setRefundError(null);
    const ok = await refundOrder(refundTarget.id, { reason: reason || undefined, restock });
    setRefundSaving(false);
    if (!ok) {
      setRefundError(
        actionErrorMessage(intl, useOrdersStore.getState().actionErrorCode),
      );
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.ordersToastRefunded));
    setRefundTarget(null);
    await refetch();
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteTarget) {
      return;
    }
    setDeleteSaving(true);
    setDeleteError(null);
    const ok = await deleteOrder(deleteTarget.id);
    setDeleteSaving(false);
    if (!ok) {
      setDeleteError(
        actionErrorMessage(intl, useOrdersStore.getState().actionErrorCode),
      );
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.ordersToastDeleted));
    setDeleteTarget(null);
    if (selectedId === deleteTarget.id) {
      closeDrawer();
    }
    await refetch();
  };

  const handleMarkPaidConfirm = async (): Promise<void> => {
    if (!markPaidTarget) {
      return;
    }
    setMarkPaidSaving(true);
    setMarkPaidError(null);
    const ok = await markOrderPaid(markPaidTarget.id);
    setMarkPaidSaving(false);
    if (!ok) {
      setMarkPaidError(
        actionErrorMessage(intl, useOrdersStore.getState().actionErrorCode),
      );
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.ordersToastMarkedPaid));
    setMarkPaidTarget(null);
    await refetch();
  };

  const handleFulfilConfirm = async (): Promise<void> => {
    if (!fulfilTarget) {
      return;
    }
    setFulfilSaving(true);
    setFulfilError(null);
    const ok = await fulfilOrder(fulfilTarget.id);
    setFulfilSaving(false);
    if (!ok) {
      setFulfilError(
        actionErrorMessage(intl, useOrdersStore.getState().actionErrorCode),
      );
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.ordersToastFulfilled));
    setFulfilTarget(null);
    await refetch();
  };

  const handleStatusEditConfirm = async (status: string, reason: string): Promise<void> => {
    if (!statusEditTarget) {
      return;
    }
    setStatusEditSaving(true);
    setStatusEditError(null);
    const ok = await overrideOrderStatus(statusEditTarget.id, status, reason || undefined);
    setStatusEditSaving(false);
    if (!ok) {
      setStatusEditError(
        actionErrorMessage(intl, useOrdersStore.getState().actionErrorCode),
      );
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.ordersToastStatusUpdated));
    setStatusEditTarget(null);
    await refetch();
  };

  const menuOrder = orders.find((o) => o.id === menuOrderId);
  const menuItems = menuOrder ? orderMenuActions(menuOrder) : [];

  const statusOptions: FilterOption[] = ORDER_STATUSES.map((status) => {
    const label = statusLabel(status);
    return { value: status, label: label ? intl.formatMessage(label) : status };
  });

  const hasActiveFilters = debouncedSearch !== '' || statusFilters.length > 0;
  const totalPages = ordersTotalPages || 1;
  const safePage = Math.min(page, totalPages);

  if (loading && orders.length === 0) {
    return (
      <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
        <Loader>
          <FormattedMessage {...adminMessages.ordersLoading} />
        </Loader>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <EmptyState
        testId="orders-error"
        title={intl.formatMessage(adminMessages.ordersErrorTitle)}
        description={intl.formatMessage(adminMessages.ordersErrorDescription)}
        action={{
          label: intl.formatMessage(adminMessages.ordersRetry),
          onClick: () => void refetch(),
        }}
      />
    );
  }

  return (
    <div className={`${styles.container} ${shared.outerContainer}`}>
      <h2 className={`gv-heading-lg ${styles.title}`}>
        <FormattedMessage {...adminMessages.ordersTitle} />
      </h2>

      <div className={styles.toolbar}>
        <ProductFilterDropdown
          testId="orders-filter-status"
          label={intl.formatMessage(adminMessages.ordersColStatus)}
          resetLabel={intl.formatMessage(adminMessages.ordersResetStatusFilter)}
          options={statusOptions}
          selected={statusFilters}
          onApply={(values) => {
            setStatusFilters(values);
            setPage(1);
          }}
        />
        <div className="gv-mode-condensed">
          <div className="gv-input gv-input-search gv-surface-bright">
            <input
              type="search"
              data-testid="orders-search"
              value={search}
              placeholder={intl.formatMessage(adminMessages.ordersSearchPlaceholder)}
              aria-label={intl.formatMessage(adminMessages.ordersSearchLabel)}
              onChange={(event) => setSearch(event.target.value)}
            />
            <span className="gv-addon" aria-hidden="true">
              <Icon name="search" size="small" />
            </span>
          </div>
        </div>
      </div>

      {orders.length === 0 ? (
        hasActiveFilters ? (
          <EmptyState
            testId="orders-no-results"
            title={intl.formatMessage(adminMessages.ordersNoResultsTitle)}
            description={intl.formatMessage(adminMessages.ordersNoResultsDescription)}
          />
        ) : (
          <EmptyState
            testId="orders-empty"
            title={intl.formatMessage(adminMessages.ordersEmptyTitle)}
            description={intl.formatMessage(adminMessages.ordersEmptyDescription)}
          />
        )
      ) : (
        <div className="gv-data-table">
          <OrdersTable
            orders={orders}
            currencySymbol={currencySymbol}
            selectedId={selectedId}
            menuOrderId={menuOrderId}
            onSelect={openDrawer}
            onMore={handleMoreClick}
          />
          <Pagination
            page={safePage}
            totalPages={totalPages}
            onPage={setPage}
            navLabel={intl.formatMessage(adminMessages.ordersTitle)}
            testIdPrefix="orders-pagination"
          />
        </div>
      )}

      {menuOrderId !== null && menuPosition && menuItems.length > 0 && (
        <ContextMenu
          items={menuItems}
          position={menuPosition}
          isMobile={isMobile}
          testIdPrefix="order"
          onSelect={handleMenuSelect}
          onClose={closeMenu}
        />
      )}

      {cancelTarget && (
        <OrderCancelModal
          saving={cancelSaving}
          errorMessage={cancelError ?? undefined}
          onConfirm={(reason) => void handleCancelConfirm(reason)}
          onClose={() => setCancelTarget(null)}
        />
      )}

      {refundTarget && (
        <OrderRefundModal
          order={refundTarget}
          currencySymbol={currencySymbol}
          saving={refundSaving}
          errorMessage={refundError ?? undefined}
          onConfirm={(opts) => void handleRefundConfirm(opts)}
          onClose={() => setRefundTarget(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.ordersDeleteTitle)}
          message={intl.formatMessage(adminMessages.ordersDeleteMessage)}
          confirmLabel={intl.formatMessage(adminMessages.ordersDeleteConfirm)}
          cancelLabel={intl.formatMessage(adminMessages.ordersCancel)}
          loadingLabel={intl.formatMessage(adminMessages.ordersDeleteLoading)}
          loading={deleteSaving}
          errorMessage={deleteError ?? undefined}
          onPrimaryClick={() => void handleDeleteConfirm()}
          onSecondaryClick={() => setDeleteTarget(null)}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {markPaidTarget && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.ordersMarkPaidTitle)}
          message={intl.formatMessage(adminMessages.ordersMarkPaidMessage)}
          confirmLabel={intl.formatMessage(adminMessages.ordersMarkPaid)}
          cancelLabel={intl.formatMessage(adminMessages.ordersCancel)}
          loadingLabel={intl.formatMessage(adminMessages.ordersMarkPaidLoading)}
          confirmVariant="primary"
          loading={markPaidSaving}
          errorMessage={markPaidError ?? undefined}
          onPrimaryClick={() => void handleMarkPaidConfirm()}
          onSecondaryClick={() => setMarkPaidTarget(null)}
          onClose={() => setMarkPaidTarget(null)}
        />
      )}

      {statusEditTarget && (
        <OrderStatusEditModal
          currentStatus={statusEditTarget.status}
          saving={statusEditSaving}
          errorMessage={statusEditError ?? undefined}
          onConfirm={(status, reason) => void handleStatusEditConfirm(status, reason)}
          onClose={() => setStatusEditTarget(null)}
        />
      )}

      {fulfilTarget && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.ordersFulfilTitle)}
          message={intl.formatMessage(adminMessages.ordersFulfilMessage)}
          confirmLabel={intl.formatMessage(adminMessages.ordersFulfil)}
          cancelLabel={intl.formatMessage(adminMessages.ordersCancel)}
          loadingLabel={intl.formatMessage(adminMessages.ordersFulfilLoading)}
          confirmVariant="primary"
          loading={fulfilSaving}
          errorMessage={fulfilError ?? undefined}
          onPrimaryClick={() => void handleFulfilConfirm()}
          onSecondaryClick={() => setFulfilTarget(null)}
          onClose={() => setFulfilTarget(null)}
        />
      )}

      <OrderDetailsDrawer
        ref={drawerRef}
        order={currentOrder}
        isOpen={selectedId !== null}
        currencySymbol={currencySymbol}
        onClose={() => {
          closeDrawer();
          clearOrder();
        }}
        onCancel={() => {
          if (currentOrder) {
            setCancelError(null);
            setCancelTarget(currentOrder);
          }
        }}
        onRefund={() => {
          if (currentOrder) {
            setRefundError(null);
            setRefundTarget(currentOrder);
          }
        }}
        onDelete={() => {
          if (currentOrder) {
            setDeleteError(null);
            setDeleteTarget(currentOrder);
          }
        }}
        onMarkPaid={() => {
          if (currentOrder) {
            setMarkPaidError(null);
            setMarkPaidTarget(currentOrder);
          }
        }}
        onFulfil={() => {
          if (currentOrder) {
            setFulfilError(null);
            setFulfilTarget(currentOrder);
          }
        }}
        onEditStatus={() => {
          if (currentOrder) {
            setStatusEditError(null);
            setStatusEditTarget(currentOrder);
          }
        }}
      />
    </div>
  );
}
